import dayjs from 'dayjs';
import { DefaultAvatarProvider } from '../../../avatar/DefaultAvatarProvider';
import { PatientFormState } from '../../../avatar/PatientFormState';
import { IAvatarPhotoColor } from '../../../avatar/PhotoAvatarOrColorPicker';
import { CareMapFactory, TREE_AVATAR } from '../../../caremap/CareMapFactory';
import { CareMapState, MYSELF } from '../../../caremap/CareMapState';
import { IContactMethod, IPatient, IRelationship } from '../../../caremap/ICareMap';
import { LabelProvider } from '../../../config/LabelProvider';
import { MixPanelEventEmitter } from '../../../metrics/MixPanelEventEmitter';
import { HistoryViewModel } from '../../../support/HistoryViewModel';
import { UserState } from '../../../user/UserState';
import { CaremapNotesModelFactory } from '../../../notes/model/CaremapNotesModelFactory';
import { DISPLAYED_FIELD_KEYS } from '../../../notes/view-note/hasExtractedFields';
import { IRecording } from '../../../recording/RecordingState';
import { deepClone } from '../../../utils/deepClone';
import { IMiniWizardConfig } from '../core/IMiniWizardConfig';
import { IMiniWizardProvider } from '../core/IMiniWizardProvider';
import { IQuestion } from '../core/IQuestion';
import { QuestionHelper } from '../core/QuestionHelper';
import { IAvatarQuestion } from '../question-types/AvatarQuestionProvider';
import { IEntityEditorQuestion } from '../question-types/EntityEditorQuestionProvider';
import { IRelationshipQuestion, IRelationshipQuestionAnswer } from '../question-types/RelationshipQuestionProvider';
import { INameQuestion, INameQuestionAnswer } from '../question-types/NameQuestionProvider';
import { ISurveryQuestionAnswers, ISurveyQuestion } from '../question-types/SurveyQuestionProvider';

export class OnboardCareRecipientWizard implements IMiniWizardProvider {
    static inject = () => [
        CareMapState,
        LabelProvider,
        HistoryViewModel,
        MixPanelEventEmitter,
        CareMapFactory,
        UserState,
        PatientFormState,
        DefaultAvatarProvider,
        CaremapNotesModelFactory
    ];

    constructor(
        private careMapState: CareMapState,
        private labelProvider: LabelProvider,
        private history: HistoryViewModel,
        private mixpanel: MixPanelEventEmitter,
        private careMapFactory: CareMapFactory,
        private userState: UserState,
        private patientForm: PatientFormState,
        private defaultAvatarProvider: DefaultAvatarProvider,
        private notesFactory: CaremapNotesModelFactory
    ) {}

    // TODO: better key, maybe onboarding-care-recipient
    wizardKey = 'new-care-recipient-2';

    private questions: {
        'care-recipient-name': INameQuestion,
        'my-name': INameQuestion,
        'patient-photo': IAvatarQuestion,
        'recipient-relationship': IRelationshipQuestion,
        'edit-recipient': IEntityEditorQuestion,
        'caregiver-needs': ISurveyQuestion
    } = {
            'care-recipient-name': {
                questionId: 'care-recipient-name',
                questionType: 'name',
                questionTitle: `Who is the care recipient?`,
                trackingHandler: (answer: INameQuestionAnswer) => {
                    this.mixpanel.track('Care Recipient Name Step Completed', () => {
                        const fieldsUsed = [
                            answer.firstName ? 'first' : '',
                            answer.lastName ? 'last' : '',
                        ].filter(field => Boolean(field));

                        return {
                            'Data Entered': fieldsUsed
                        };
                    });
                }
            },
            'my-name': {
                questionId: 'my-name',
                questionType: 'name',
                questionTitle: 'What is your name?'
            },
            'patient-photo': {
                questionId: 'patient-photo',
                questionType: 'avatar',
                questionTitle: '',
                subTitle: '(optional)',
                trackingHandler: (answer: IAvatarPhotoColor) => {
                    const { photo, color, avatar } = answer;
                    this.mixpanel.track('Care Recipient Visual Step Completed', () => ({
                        color: color || '',
                        avatar: avatar || '',
                        photo: photo ? 'uploaded' : ''
                    }));
                }
            },
            'recipient-relationship': {
                questionId: 'recipient-relationship',
                questionType: 'household',
                questionTitle: '',
                recipientFirstName: '',
                trackingHandler: (answer: IRelationshipQuestionAnswer) => {
                    const relationToCareRecipient = answer.relationship?.value || answer.relationship?.label;
                    this.mixpanel.track('Relation to Recipient Step Complete', () => ({
                        relationToCareRecipient,
                        'primary caregiver': Boolean(answer.primaryCaregiver)
                    }));
                }
            },
            'edit-recipient': {
                questionId: 'edit-recipient',
                questionType: 'entity-editor',
                questionTitle: 'Complete Care Recipient Profile'
            },
            'caregiver-needs': {
                questionId: 'caregiver-needs',
                questionType: 'survey',
                questionTitle: '',
                subTitle: 'Choose all that apply.',
                options: [
                    'Organizing Care Information',
                    'Communicating with Friends & Family',
                    'Coordinating Help from Friends & Family',
                    'Finding Professional Help',
                    'Finding Financial Support',
                    'Something Else'
                ],
                trackingHandler: (answer: ISurveryQuestionAnswers) => {
                    this.mixpanel.track('Most Helpful Poll Step Completed', () => {
                        return {
                            selection: answer
                        };
                    });
                }
            }
        };

    // NOTE: this is the order the steps will be in
    private buildQuestions = () => {
        const questions: IQuestion[] = deepClone([
            this.questions['care-recipient-name'],
            this.questions['patient-photo'],
            this.questions['recipient-relationship'],
            this.questions['caregiver-needs'],
            this.questions['edit-recipient']
        ]);

        const helper = new QuestionHelper(questions);

        if (this.careMapState.isUserThePatient()) {
            helper.getQuestion(this.questions['care-recipient-name']).disabled = true;
            this.applySelfLabels();
            helper.getQuestion(this.questions['recipient-relationship']).disabled = true;
        }
        else {
            this.applySomeoneElseLabels();

            helper.getQuestion(this.questions['care-recipient-name']).answer = {
                firstName: this.careMapState.getPatientFirstName(),
                lastName: this.careMapState.getPatientLastName()
            };
        }

        const patient = this.careMapState.getActiveCaremap()!.patient;
        const { photo, color, avatar } = patient;
        if (photo || color || avatar ){
            helper.getQuestion(this.questions['patient-photo']).answer = {
                photo, color, avatar
            };
        }

        const member = this.careMapState.getHouseholdMemberByUserEmail(this.userState.state.email!);
        if (member){
            helper.getQuestion(this.questions['recipient-relationship']).answer = {
                primaryCaregiver: member.primaryCaregiver,
                relationship: {
                    label: member.relationship || '',
                    value: member.relationshipId
                }
            };

        } else {
            helper.getQuestion(this.questions['recipient-relationship']).answer = {
                primaryCaregiver: true
            };
        }

        this.applyPatientPhotoLabel(helper);

        this.applyRecipientEditorAnswer(helper, this.careMapState.getActiveCaremap()!.patient);
        this.patientForm.subscribe(state => {
            if (state.patient) {
                this.applyRecipientEditorAnswer(helper, state.patient);
            }
        });

        helper.getQuestion(this.questions['caregiver-needs']).answer = this.careMapState.getInitialWizardAnswer('caregiver-needs');

        return questions;
    };

    create = (params: Map<string, string>): IMiniWizardConfig => {
        const careMapId = params.get('id');
        if (!careMapId){
            // ideally a 500 page, but shouldn't happen so not a priority right now
            throw new Error(`${careMapId} is required`);
        }
        if (!this.careMapState.getCareMapById(careMapId)){
            // ideally a 404 page, but shouldn't happen so not a priority right now
            throw new Error(`could not find a caremap with id ${careMapId}`);
        }

        this.careMapState.setActiveCaremap(careMapId);

        const redirectTo = params.get('redirectTo') || '/caregiver';

        return {
            title: 'New Care Recipient',
            questions: this.buildQuestions(),
            skipToFirstInvalidStep: !params.get('startFromBeginning'),
            onAnswered: (answeredQuestion, allQuestions) => {
                const answerHelper = new QuestionHelper(allQuestions);

                if (answerHelper.isSameQuestionId(answeredQuestion, this.questions['patient-photo'])) {
                    const {photo, avatar, color } = answerHelper.getAnswer(this.questions['patient-photo'])!;
                    this.careMapState.updatePatient({
                        ...this.careMapState.getActiveCaremap()!.patient,
                        photo,
                        avatar,
                        color
                    });

                    this.applyPatientPhotoProps(answerHelper);
                    this.applyRecipientRelationshipProps(answerHelper);
                    this.applyCaregiverNeedsProps(answerHelper);
                }

                if (answerHelper.isSameQuestionId(answeredQuestion, this.questions['care-recipient-name'])){
                    const { firstName, lastName } = answerHelper.getAnswer(this.questions['care-recipient-name'])!;
                    this.careMapState.updatePatient({
                        ...this.careMapState.getActiveCaremap()!.patient,
                        firstName,
                        lastName
                    });

                    this.applyPatientPhotoLabel(answerHelper);
                }
                else if (answerHelper.isSameQuestionId(answeredQuestion, this.questions['recipient-relationship'])) {
                    const relationshipAnswer = answerHelper.getAnswer(this.questions['recipient-relationship']);
                    const member = this.careMapState.getHouseholdMemberByUserEmail(this.userState.state.email!);

                    if (this.careMapState.getActiveCaremap()!.household.members.length === 0 || !member) {
                        const contactMethods: IContactMethod[] = [{type: 'email', value: this.userState.state.email || ''}];
                        if (this.userState.state.phone) {
                            const p = this.userState.state.phone;
                            const formatted = /^\d{10}$/.test(p) ? `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}` : p;
                            contactMethods.push({type: 'mobile-phone', value: formatted});
                        }
                        this.careMapState.addHouseholdMembers([{
                            id: this.careMapFactory.getNodeId(),
                            userEmail: this.userState.state.email,
                            firstName: this.userState.state.firstName,
                            lastName: this.userState.state.lastName,
                            relationship: relationshipAnswer?.relationship?.label,
                            relationshipId: relationshipAnswer?.relationship?.value ? (relationshipAnswer.relationship.value as IRelationship) : undefined,
                            primaryCaregiver: relationshipAnswer?.primaryCaregiver,
                            address: this.userState.state.address,
                            avatar: this.defaultAvatarProvider.getDefaultAvatar({ careMap: this.careMapState, preferredAvatar: TREE_AVATAR }),
                            contactMethods
                        }]);
                    } else {
                        this.careMapState.updateHouseholdMember({
                            ...member,
                            avatar: member.avatar || this.defaultAvatarProvider.getDefaultAvatar({ careMap: this.careMapState, preferredAvatar: TREE_AVATAR }),
                            relationship: relationshipAnswer?.relationship?.label,
                            relationshipId: relationshipAnswer?.relationship?.value ? (relationshipAnswer.relationship.value as IRelationship) : undefined,
                            primaryCaregiver: relationshipAnswer?.primaryCaregiver
                        });
                    }
                }
                else if (answerHelper.isSameQuestionId(answeredQuestion, this.questions['edit-recipient'])) {
                    const answer = answerHelper.getAnswer(this.questions['edit-recipient'])!;
                    this.careMapState.updatePatient({
                        ...this.careMapState.getActiveCaremap()!.patient,
                        ...answer
                    });
                } else if (answerHelper.isSameQuestionId(answeredQuestion, this.questions['caregiver-needs'])) {
                    const answer = answerHelper.getAnswer(this.questions['caregiver-needs']);
                    this.careMapState.answerInitialWizardQuestion({questionId: 'caregiver-needs', answer});
                }

                return allQuestions;
            },
            onComplete: async (allQuestions) => {
                const answerHelper = new QuestionHelper(allQuestions);

                this.careMapState.updatePatient(
                    this.careMapState.getActiveCaremap()!.patient,
                    { markEssentialMapComplete: true, initialWizardAnswers: [] }
                );

                this.careMapState.completeOnboardingStep('add-care-recipient');

                this.careMapState.startOnboardingStep('household-members');
                this.history.push(redirectTo);

                const mostHelpfulSelection = answerHelper.getAnswer(this.questions['caregiver-needs']);
                const relationToCareRecipientAnswer = answerHelper.getAnswer(this.questions['recipient-relationship']);

                // Detect whether voice was the method used by checking for an intro
                // recording note on this caremap. The intro note is the durable record
                // of the user picking voice, regardless of whether voice captured anything.
                const introNote = this.notesFactory.forActiveCaremap().getNotes().find(n => {
                    const rec = n.otherAttributes?.recording as IRecording | undefined;
                    return rec?.summaryPrompt === 'intro';
                });
                const method: 'voice' | 'type' = introNote ? 'voice' : 'type';
                const introExtractedFields = (introNote?.otherAttributes?.recording as IRecording | undefined)?.extractedFields;
                const voiceFieldsCaptured = method === 'voice'
                    ? DISPLAYED_FIELD_KEYS.filter(key => {
                        const value = introExtractedFields?.[key];
                        return Array.isArray(value) ? value.length > 0 : Boolean(value);
                    })
                    : undefined;

                this.mixpanel.track('New Care Recipient Initial Questionnaire Completed',  () => {
                    const patient = this.careMapState.getActiveCaremap()!.patient;
                    const contactMethods = this.careMapState.cleanupContactMethods(patient.contactMethods);
                    const dob = this.careMapState.getPatientDOB();
                    return {
                        mostHelpfulSelection,
                        relationToCareRecipient: (relationToCareRecipientAnswer?.relationship?.label || relationToCareRecipientAnswer?.relationship?.value) || 'self',
                        'by primary caregiver': Boolean(relationToCareRecipientAnswer?.primaryCaregiver),
                        '# of Contact Methods': contactMethods.length,
                        'Contact Methods': contactMethods.map(m => m.type),
                        '# of Allergies': (patient.allergies || []).length,
                        '# of Diagnoses': (patient.diagnoses || []).length,
                        'Diagnoses Privacy': patient.diagnosesIsPublic ? 'public' : 'private',
                        'Birth Year': dob ? dayjs(dob).year() : undefined,
                        'Age': dob ? dayjs().diff(dob, 'year') : undefined,
                        'DOB Added': Boolean(dob),
                        Method: method,
                        ...(voiceFieldsCaptured ? { 'Voice Fields Captured': voiceFieldsCaptured } : {})
                    };
                });
            }
        };
    };

    start = (careMapId: string) => {
        this.history.push(`/q/${this.wizardKey}?id=${careMapId}`);
    };

    private applySelfLabels = () => {
        this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable = true;
    };

    private applySomeoneElseLabels = () => {
        this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable = false;
    };

    private applyPatientPhotoLabel = (answerHelper: QuestionHelper) => {
        let recipientName = '';

        const isPatientSelf = this.careMapState.isUserThePatient();

        if(!isPatientSelf){
            recipientName = this.careMapState.getActiveCaremap()?.patient?.firstName || '';
        }
        // else (if patient is self) then the name isn't used by the label

        const label = this.labelProvider.getLabel('PHOTO_FOR_CARE_RECIPIENT', { careRecipient: recipientName });
        answerHelper.getQuestion(this.questions['patient-photo']).questionTitle = label;
    };

    private applyPatientPhotoProps = (answerHelper: QuestionHelper) => {
        const label = this.labelProvider.getLabel('PHOTO_FOR_CARE_RECIPIENT', { careRecipient: this.getPatientFirstName() });
        answerHelper.getQuestion(this.questions['patient-photo']).questionTitle = label;
    };

    private applyRecipientRelationshipProps = (answerHelper: QuestionHelper) => {
        const question = answerHelper.getQuestion(this.questions['recipient-relationship']);
        const recipientFirstName = this.getPatientFirstName();

        question.recipientFirstName = recipientFirstName;
        question.questionTitle = `What is your relationship to ${recipientFirstName}?`;
    };

    private applyRecipientEditorAnswer = (answerHelper: QuestionHelper, patientFormState: IPatient) => {
        const question = answerHelper.getQuestion(this.questions['edit-recipient']);
        question.answer = patientFormState;
    };

    private getPatientFirstName = () => {
        return this.careMapState.getPatientFirstName();
    };

    private isCareMapForSomeoneElse = () => {
        return !(this.careMapState.getActiveCaremap()?.initialWizardAnswers['recipient']?.value === MYSELF.value);
    };

    private applyCaregiverNeedsProps = (answerHelper: QuestionHelper) => {
        const question = answerHelper.getQuestion(this.questions['caregiver-needs']);
        const withClause = (this.isCareMapForSomeoneElse() ? ` with ${this.getPatientFirstName()}'s care` : '');
        question.questionTitle = `What would help you the most right now${withClause}?`;
    };
};
